import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as types from '../../constants/actionTypes';
import { actions as locationActions } from '../../actions/locationActions';
import { actions as locationDashboardActions } from '../../actions/locationDashboardActions';
import { actions as locationTaskActions } from '../../actions/locationTaskActions';
import { actions as locationContactActions } from '../../actions/locationContactActions';
import { actions as filesActions } from '../../actions/filesActions';
import { MOBILE_WINDOW_BREAKPOINT, LOCATION_DASHBOARD_URL_PARAMS as URLParams } from '../../constants/constants';
import URLSearchParams from '@ungap/url-search-params'; // URLSearchParams polyfill for IE11
import Notification from '../Common/Notification';
import './LocationDashboardPage.scss';
import DashboardWidget from '../Common/DashboardWidget/DashboardWidget';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Projects from './Widgets/Projects';
import Summary from './Widgets/Summary';
import Milestones from './Widgets/Milestones';
import LocationDetails from './Widgets/LocationDetails';
import TaskCompletion from './Widgets/TaskCompletion';
import SalesGoals from './Widgets/SalesGoals';
import LocationSelector from './Widgets/LocationSelector';
import LocationSelectorDrawer from '../Common/LocationSelector/LocationSelectorDrawer';
import Routes from '../../constants/routes';
import AssignTasksDrawer from './Widgets/AssignTasksDrawer';
import Modal61 from '../Common/Modal/Modal61';

export class LocationDashboard extends Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			selectedLocation: { id: '', name: '', abbreviation: '', openingDate: '' },
			followedDepartmentIds: [],
			isProjectWidgetActive: false,
			isGoalWidgetActive: false,
			isLocOpenDateTodayOrFuture: false,
			isFetchingFilters: true, // default true - the widgets do not loading until false
			isSearchingLocations: false,
			isLocationSelectorDrawerOpen: false,
			unassignedTasks: 0,
			showAssignTasksDrawer: false,
			locationKey: '',
		};

		this.waiverModal = React.createRef();
	}

	onAssignTasks = () => {
		this.setState({ showAssignTasksDrawer: true });
	};

	onAssignTasksComplete = () => {
		this.setState({ showAssignTasksDrawer: false });
	};

	onAssignTasksCancel = () => {
		this.setState({ showAssignTasksDrawer: false });
	};

	isMobile = () => {
		return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
	};

	componentDidUpdate = async (prevProps, prevState) => {
		if (
			(this.props.user.currentSubscriptionId && this.props.user.currentSubscriptionId != prevProps.user.currentSubscriptionId) ||
			(this.state.selectedLocation.id && this.state.selectedLocation.id != prevState.selectedLocation.id)
		) {
			await this.loadFilters();
		}

		// With the react upgrade, this value was not reliably triggering within componentDidMount()
		const reduxKey =
			this.props.taskListState &&
			this.props.taskListState.searchParams &&
			this.props.taskListState.searchParams.locationKeys &&
			this.props.taskListState.searchParams.locationKeys.length > 0
				? this.props.taskListState.searchParams.locationKeys[0].toString()
				: '';

		const prevReduxKey =
			prevProps.taskListState &&
			prevProps.taskListState.searchParams &&
			prevProps.taskListState.searchParams.locationKeys &&
			prevProps.taskListState.searchParams.locationKeys.length > 0
				? prevProps.taskListState.searchParams.locationKeys[0].toString()
				: '';

		if (reduxKey != prevReduxKey) {
			await this.getUnassignedTasksForProjectCoaches();
		}
	};

	componentDidMount = async () => {
		await this.loadFilters();
		await this.getUnassignedTasksForProjectCoaches();
	};

	/**
	 * Wrapper for retrieving the selected location from Redux.  We're now
	 * storing it there instead of in local component state, because it has to
	 * share that selected location state with the Task List.
	 *
	 * This function also encapsulates the logic to override the normal operation
	 * of sharing the redux selected location, by instead selecting the location
	 * passed to this page via it's URL parameters.
	 */
	getSelectedLocationKey = () => {
		if (this.props.urlParams && this.props.urlParams.locationKey) {
			return this.props.urlParams.locationKey.toString();
		} else {
			const reduxKey =
				this.props.taskListState &&
				this.props.taskListState.searchParams &&
				this.props.taskListState.searchParams.locationKeys &&
				this.props.taskListState.searchParams.locationKeys.length > 0
					? this.props.taskListState.searchParams.locationKeys[0].toString()
					: '';

			return reduxKey;
		}
	};

	/**
	 * Function to handle firing the action to update the taskListState redux
	 * store with the location selected from the dashboard's location selector
	 */
	setSelectedLocationKey = (newLocationKey) => {
		if (newLocationKey && newLocationKey.trim().length > 0) {
			this.props.locationTaskActions.setLocationKeys([newLocationKey]);
		}
	};

	loadFilters = async () => {
		try {
			this.setState({ isFetchingFilters: true });
			const response = await this.props.locationActions.loadDashboardFilters(this.getSelectedLocationKey());
			if (response.type === types.LOAD_LOCATION_DASHBOARD_FILTERS_SUCCESS) {
				if (response.payload.location) {
					this.setState(
						{
							selectedLocation: response.payload.location,
							followedDepartmentIds: response.payload.followedDepartmentIds,
							isProjectWidgetActive: response.payload.isProjectWidgetActive,
							isGoalWidgetActive: response.payload.isGoalWidgetActive,
							isLocOpenDateTodayOrFuture: response.payload.isLocOpenDateTodayOrFuture,
							projectTags: response.payload.tags,
						},
						this.setSelectedLocationKey(response.payload.location.id)
					);
				} else {
					Notification('error', 'No Locations', 'There was no default location found');
				}
			} else if (response.type === types.LOAD_LOCATION_DASHBOARD_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isFetchingFilters: false });
		}
	};

	getUnassignedTasksForProjectCoaches = async () => {
		try {
			const locKey = this.getSelectedLocationKey();
			if (!locKey) {
				return;
			}

			const response = await this.props.locationDashboardActions.getUnassignedTasksForProjectCoaches(this.getSelectedLocationKey());

			if (response.type === types.DASHBOARD_UNASSIGNED_TASKS_FOR_COACHES_SUCCESS) {
				const unassignedTasks = response.payload.unassignedTasks;

				if (unassignedTasks > 0) {
					this.setState({
						unassignedTasks,
						locationKey: this.getSelectedLocationKey(),
					});

					const message = `There are ${unassignedTasks} tasks that are unassigned and due soon. Click here to assign tasks by job title. `;

					Notification('info', 'Upcoming Unassigned Tasks', message, true, 'toast-bottom-left', this.onAssignTasks, '60000', '60000', true);
				}
			} else if (response.type === types.DASHBOARD_UNASSIGNED_TASKS_FOR_COACHES_FAILURE) {
				const errorMessage = response.payload.message;
				Notification('error', 'Error', errorMessage);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	updateFollowedDepartmentIds = (newIds) => {
		this.setState({ followedDepartmentIds: newIds });
	};

	onCompleteLocationSelectorDrawer = (selectedLocations) => {
		this.setState({ selectedLocation: selectedLocations[0], isLocationSelectorDrawerOpen: false }, () => {
			if (this.props.urlParams && this.props.urlParams.locationKey) {
				// if there is a location param in the urlparams, get rid of it
				this.props.history.push(Routes.DashboardLocation);
			}
		});
	};

	onCancelLocationSelectorDrawer = () => {
		this.setState({ isLocationSelectorDrawerOpen: false });
	};

	onOpenLocationSelectorDrawer = () => {
		this.setState({ isLocationSelectorDrawerOpen: true });
	};

	onRequestWaiver = () => {
		this.waiverModal.current.show();
	};

	render() {
		const isNotLoading = !this.state.isSearchingLocations && !this.state.isFetchingFilters;
		const isLocationReady = this.getSelectedLocationKey();
		const showProjectWidget = this.state.isProjectWidgetActive && isLocationReady && isNotLoading;
		const showSummaryWidget = isNotLoading && isLocationReady;
		const showMilestonesWidget = this.state.isLocOpenDateTodayOrFuture && isLocationReady && isNotLoading;
		const showTaskCompletionWidget = isNotLoading && isLocationReady;
		const showSalesGoalsWidget = this.state.isGoalWidgetActive && isNotLoading && isLocationReady;
		const showLocationDetailsWidget = isNotLoading && isLocationReady;

		return (
			<div className='location-dashboard no-gutters'>
				<LoadingIndicator isLoading={this.state.isFetchingFilters} isFullscreen={true}>
					<Fragment>
						{this.state.isFetchingFilters === false && (
							<DashboardWidget bgColor='gray'>
								<LocationSelector
									onChangeLocation={this.onOpenLocationSelectorDrawer}
									selectedLocation={this.state.selectedLocation}
								/>
							</DashboardWidget>
						)}
					</Fragment>

					{showProjectWidget && (
						<DashboardWidget bgColor='white'>
							<Projects
								subscriptionId={this.props.subscriptionId}
								locationKey={this.getSelectedLocationKey()}
								locationDashboardActions={this.props.locationDashboardActions}
								isMobile={this.isMobile()}
								currentUser={this.props.user}
								onRequestWaiver={this.onRequestWaiver}
								projectTags={this.state.projectTags}
							/>
						</DashboardWidget>
					)}
					{showSummaryWidget && this.state.isFetchingFilters === false && (
						<DashboardWidget bgColor='gray'>
							<Summary
								locationKey={this.getSelectedLocationKey()}
								isLocOpenDateTodayOrFuture={this.state.isLocOpenDateTodayOrFuture}
								followedDepartmentIds={this.state.followedDepartmentIds}
								isMobile={this.isMobile()}
								subscriptionId={this.props.subscriptionId}
								updateFollowedDepartmentIds={this.updateFollowedDepartmentIds}
							/>
						</DashboardWidget>
					)}
					{showMilestonesWidget && (
						<DashboardWidget bgColor='white'>
							<Milestones
								locationKey={this.getSelectedLocationKey()}
								location={this.state.selectedLocation}
								locationDashboardActions={this.props.locationDashboardActions}
								locationTaskActions={this.props.locationTaskActions}
								filesActions={this.props.filesActions}
							/>
						</DashboardWidget>
					)}
					{showTaskCompletionWidget && (
						<DashboardWidget bgColor='gray'>
							<TaskCompletion
								subscriptionId={this.props.subscriptionId}
								locationKey={this.getSelectedLocationKey()}
								locationDashboardActions={this.props.locationDashboardActions}
								isMobile={this.isMobile()}
							/>
						</DashboardWidget>
					)}
					{showSalesGoalsWidget && (
						<DashboardWidget bgColor='white'>
							<SalesGoals locationKey={this.getSelectedLocationKey()} locationDashboardActions={this.props.locationDashboardActions} />
						</DashboardWidget>
					)}
					{showLocationDetailsWidget && (
						<DashboardWidget bgColor={this.state.isGoalWidgetActive ? 'gray' : 'white'}>
							<LocationDetails
								locationKey={this.getSelectedLocationKey()}
								locationDashboardActions={this.props.locationDashboardActions}
								locationTaskActions={this.props.locationTaskActions}
								locationActions={this.props.locationActions}
								locationContactActions={this.props.locationContactActions}
								filesActions={this.props.filesActions}
								subscriptionId={this.props.subscriptionId}
								history={this.props.history}
							/>
						</DashboardWidget>
					)}
					{this.state.showAssignTasksDrawer && (
						<AssignTasksDrawer
							locationDashboardActions={this.props.locationDashboardActions}
							onComplete={this.onAssignTasksComplete}
							onCancel={this.onAssignTasksCancel}
							locationKey={this.state.locationKey}
						/>
					)}
				</LoadingIndicator>
				<LocationSelectorDrawer
					onComplete={this.onCompleteLocationSelectorDrawer}
					onCancel={this.onCancelLocationSelectorDrawer}
					isSingleSelect={true}
					isOpen={this.state.isLocationSelectorDrawerOpen}
				/>

				<Modal61
					title='Waiver Request'
					body={<span>Your waiver request has started. Please check your email for any next steps to complete.</span>}
					primaryButtonText='Ok'
					isCentered={true}
					ref={this.waiverModal}
				/>
			</div>
		);
	}
}

LocationDashboard.propTypes = {
	locationActions: PropTypes.object.isRequired,
	locationDashboardActions: PropTypes.object.isRequired,
	locationTaskActions: PropTypes.object.isRequired,
	filesActions: PropTypes.object.isRequired,
	locationContactActions: PropTypes.object.isRequired,
	windowWidth: PropTypes.number,
	subscriptionId: PropTypes.string.isRequired,
	urlParams: PropTypes.object.isRequired,
	history: PropTypes.object.isRequired,
	taskListState: PropTypes.object.isRequired,
	user: PropTypes.object,
};

function mapStateToProps(state, ownProps) {
	let urlParams = {
		locationKey: '',
	};
	const searchParams = new URLSearchParams(ownProps.location.search);
	if (searchParams.has(URLParams.locationKey)) {
		urlParams.locationKey = searchParams.get(URLParams.locationKey);
	}

	const subId = state.user.currentSubscriptionId ? state.user.currentSubscriptionId.toString() : '';

	return {
		windowWidth: state.window.width,
		subscriptionId: subId,
		urlParams: urlParams,
		history: ownProps.history,
		taskListState: state.taskListState,
		user: state.user,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		locationActions: bindActionCreators(locationActions, dispatch),
		locationDashboardActions: bindActionCreators(locationDashboardActions, dispatch),
		locationTaskActions: bindActionCreators(locationTaskActions, dispatch),
		filesActions: bindActionCreators(filesActions, dispatch),
		locationContactActions: bindActionCreators(locationContactActions, dispatch),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(LocationDashboard);
